import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from '@services/api/api.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { Observable, Subscription } from 'rxjs';
import {
  AiCapabilityCategory,
  AiChatMessage,
  AiConversationSummary,
  AiGreetingDay,
  AiOpenLeadRequest,
  AiSuggestion,
} from '../../models/ai-assistant.models';
import { AiAssistantStateService } from '../../services/ai-assistant-state.service';
import { AiAssistantComposerComponent, AiComposerSubmit } from '../ai-assistant-composer/ai-assistant-composer.component';
import { AiAssistantEmptyStateComponent } from '../ai-assistant-empty-state/ai-assistant-empty-state.component';
import { AiConfirmEvent, AiAssistantMessageListComponent } from '../ai-assistant-message-list/ai-assistant-message-list.component';
import { ProvidedField } from '../ai-assistant-required-fields/ai-assistant-required-fields.component';
import { personalGreeting } from '../../utils/greeting.util';
import { friendlyTimestamp } from '../../utils/ai-date.util';

/**
 * Slide-out chat shell: header (new chat / history / close), the thread (or welcome
 * empty-state), an optional notice banner, and the composer. Orchestrates the
 * {@link AiAssistantStateService}; presentational children stay dumb.
 */
@Component({
  selector: 'app-ai-assistant-panel',
  standalone: true,
  imports: [CommonModule, AiAssistantMessageListComponent, AiAssistantComposerComponent, AiAssistantEmptyStateComponent],
  templateUrl: './ai-assistant-panel.component.html',
  styleUrls: ['./ai-assistant-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantPanelComponent implements OnInit, OnDestroy {
  readonly isOpen$: Observable<boolean> = this.state.isOpen$;
  readonly messages$: Observable<AiChatMessage[]> = this.state.messages$;
  readonly isBusy$: Observable<boolean> = this.state.isBusy$;
  readonly notice$: Observable<string | null> = this.state.notice$;
  readonly suggestions$: Observable<AiSuggestion[]> = this.state.suggestions$;
  readonly capabilities$: Observable<AiCapabilityCategory[]> = this.state.capabilities$;

  seedText: string | null = null;
  /** True while the welcome composer has a draft — hides the welcome helper paragraph. */
  welcomeTyping = false;
  showHistory = false;
  historyLoading = false;
  conversations: AiConversationSummary[] = [];

  /** Personalized welcome greeting, e.g. "Good morning, Rosina." (client fallback until /greeting). */
  greeting = '';
  /** The offer shown under the greeting; overridden by the server greeting when it arrives. */
  greetingPrompt = 'Would you like me to run through your day?';
  /** Quick day snapshot for the welcome badge (from `GET /greeting`); null hides it. */
  daySnapshot: AiGreetingDay | null = null;
  private greetingSub?: Subscription;
  private openSub?: Subscription;

  /** Current panel width in px (user-resizable; persisted across sessions). */
  panelWidth = AiAssistantPanelComponent.DEFAULT_WIDTH;
  private dragStartX = 0;
  private dragStartWidth = 0;
  private dragging = false;

  private static readonly DEFAULT_WIDTH = 400;
  private static readonly MIN_WIDTH = 340;
  private static readonly WIDTH_STORAGE_KEY = 'ai-assistant:panel-width';

  constructor(
    private readonly state: AiAssistantStateService,
    private readonly router: Router,
    private readonly lmService: LeadManagementService,
    private readonly api: ApiService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.greeting = personalGreeting(this.api.getUserName());
    this.panelWidth = this.clampWidth(this.readStoredWidth() ?? AiAssistantPanelComponent.DEFAULT_WIDTH);
  }

  ngOnInit(): void {
    // Welcome chips + capability categories (FRONTEND_HANDOFF §3, §8). Cached per session.
    this.state.loadConfig();

    // Timezone-aware greeting + day snapshot (§0a). Overrides the instant client greeting when
    // it arrives; the panel fetches it on open, so the welcome shows immediately either way.
    this.greetingSub = this.state.greeting$.subscribe(greeting => {
      if (!greeting) {
        return;
      }
      if (greeting.greeting) {
        this.greeting = greeting.greeting;
      }
      if (greeting.prompt) {
        this.greetingPrompt = greeting.prompt;
      }
      this.daySnapshot = greeting.day ?? null;
      this.cdr.markForCheck();
    });

    // Opening the dialog starts a clean slate (see state.open) — drop any leftover composer seed
    // (e.g. a welcome suggestion) so the fresh thread's composer isn't pre-filled.
    this.openSub = this.isOpen$.subscribe(open => {
      if (open) {
        this.seedText = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.greetingSub?.unsubscribe();
    this.openSub?.unsubscribe();
    // Guard against a drag that outlived the component.
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    document.body.style.userSelect = '';
  }

  // ---- Panel resizing ---------------------------------------------------------

  /** Largest width that still leaves a sliver of the app visible (full-width on mobile). */
  private maxWidth(): number {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return vw <= 480 ? vw : Math.min(1000, vw - 80);
  }

  private clampWidth(width: number): number {
    return Math.round(Math.max(AiAssistantPanelComponent.MIN_WIDTH, Math.min(this.maxWidth(), width)));
  }

  /** True during an active edge drag — used to suppress the width transition. */
  get isResizing(): boolean {
    return this.dragging;
  }

  /** Begin an edge drag-resize. Listeners are bound for the duration of the gesture. */
  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartWidth = this.panelWidth;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  // Arrow functions so `this` stays bound when used as document listeners.
  private readonly onResizeMove = (event: MouseEvent): void => {
    if (!this.dragging) {
      return;
    }
    // Panel is anchored right; dragging the left edge left (smaller clientX) widens it.
    const delta = this.dragStartX - event.clientX;
    this.panelWidth = this.clampWidth(this.dragStartWidth + delta);
    this.cdr.markForCheck();
  };

  private readonly onResizeEnd = (): void => {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    this.storeWidth(this.panelWidth);
  };

  private readStoredWidth(): number | null {
    try {
      const raw = localStorage.getItem(AiAssistantPanelComponent.WIDTH_STORAGE_KEY);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private storeWidth(width: number): void {
    try {
      localStorage.setItem(AiAssistantPanelComponent.WIDTH_STORAGE_KEY, String(width));
    } catch {
      /* localStorage unavailable (private mode) — size is best-effort */
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.state.isOpen) {
      if (this.showHistory) {
        this.showHistory = false;
      } else {
        this.state.close();
      }
    }
  }

  close(): void {
    this.showHistory = false;
    this.state.close();
  }

  newChat(): void {
    this.showHistory = false;
    this.seedText = null;
    this.state.startNewChat();
  }

  onSend(text: string): void {
    this.state.sendMessage(text);
  }

  /** Composer submit — carries the typed text plus any attached images (base64 data URLs). */
  onComposerSend(payload: AiComposerSubmit): void {
    this.state.sendMessage(payload.text, payload.images);
  }

  onConfirm(event: AiConfirmEvent): void {
    const message = event?.message;
    if (message?.pendingAction) {
      this.state.confirmAction(message.id, message.pendingAction, event.values);
    }
  }

  onCancel(message: AiChatMessage): void {
    if (message.pendingAction) {
      this.state.cancelAction(message.id, message.pendingAction);
    }
  }

  /** Welcome composer draft changed — hide the helper paragraph once there's any text. */
  onWelcomeDraft(text: string): void {
    this.welcomeTyping = (text || '').trim().length > 0;
  }

  onRetry(message: AiChatMessage): void {
    this.state.retryMessage(message.id);
  }

  /**
   * The user filled in the missing required fields via the inline dropdowns. Relay the
   * selections back to the assistant as a reply so it can finish the blocked create.
   */
  onProvideFields(values: ProvidedField[]): void {
    if (!values?.length) {
      return;
    }
    const reply = values.map(v => `${v.label}: ${v.value}`).join(', ');
    this.state.sendMessage(reply);
  }

  /**
   * Open a lead from an assistant card. Round 5: when the lead carries a `project_id` we open it
   * inside its PROJECT — `/projects/edit_/<projectId>/lead?id=<leadId>` opens the project's Leads
   * tab AND opens that exact lead (see project-leads `checkLeadIdInParams`). A lead with no project
   * falls back to the Leads screen — navigating with `?id=<leadId>` makes the leads list load that
   * lead and open its edit panel (see Leads `checkIdInParams`), and an empty id opens the plain
   * list. Tolerates a bare leadId string for legacy callers.
   *
   * The panel stays open (non-modal) so Remmi AI remains docked beside the opened record —
   * the conversation is always within reach. Dismiss via Close, Esc, or the FAB toggle.
   */
  onViewLead(request: AiOpenLeadRequest | string): void {
    const req: AiOpenLeadRequest = typeof request === 'string' ? { leadId: request } : request || { leadId: '' };
    const leadId = (req.leadId || '').trim();
    const projectId = (req.projectId || '').trim();

    this.lmService.isLeadUpdate(true);

    if (projectId) {
      // Open the lead within its project's Leads tab; `?id=<leadId>` opens the lead itself.
      this.router.navigate(['/projects/edit_/' + projectId + '/lead'], leadId ? { queryParams: { id: leadId } } : {});
      return;
    }

    if (leadId) {
      this.router.navigate(['/leads'], { queryParams: { id: leadId } });
    } else {
      this.router.navigate(['/leads']);
    }
  }

  /**
   * Open a lead straight on its Appointment tab (from an appointment/inspection row). Sets a
   * one-shot hint the lead detail consumes on load, then opens the lead the usual way.
   */
  onViewLeadAppointments(leadId: string): void {
    if (!leadId) {
      return;
    }
    this.lmService.aiLeadTab = 'appointment';
    // Appointment rows always open the LEAD (on its Appointment tab), never the project.
    this.onViewLead({ leadId });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.loadHistory();
    }
  }

  loadConversation(id: string): void {
    this.showHistory = false;
    // Drop any leftover composer seed so the loaded conversation's composer starts empty.
    this.seedText = null;
    this.state.loadConversation(id);
  }

  trackByConversation(_index: number, item: AiConversationSummary): string {
    return item.id;
  }

  /** Friendly last-active label for a conversation, e.g. "Today, 3:00 PM". */
  historyTime(c: AiConversationSummary): string {
    return friendlyTimestamp(c?.updated_at || c?.created_at);
  }

  /** Two-letter glyph for the history row avatar, derived from the conversation title. */
  historyInitial(c: AiConversationSummary): string {
    const title = (c?.title || '').trim();
    return title ? title.charAt(0).toUpperCase() : '·';
  }

  private loadHistory(): void {
    this.historyLoading = true;
    this.conversations = [];
    this.state.listConversations().subscribe({
      next: list => {
        this.conversations = list || [];
        this.historyLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.historyLoading = false;
        this.cdr.markForCheck();
      },
    });
  }
}
